"use client";

import { Loader2, ChevronRight } from "lucide-react";
import { Typography } from "@/components/common/ui/Typography";
import type { BaseCarrier } from "./types";

export function CarrierLoadingCard({ carrier }: { carrier: BaseCarrier }) {
  return (
    <div className="@container relative bg-white rounded-lg border border-blue-200 transition-all shadow-[0_1px_3px_rgba(0,0,0,0.06)] overflow-hidden">
      {/* shimmer effect overlay */}
      <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-blue-50/80 to-transparent pointer-events-none" />

      <div className="flex items-center gap-4 px-4 py-3 relative">
        {/* Checkbox spacer */}
        <div className="w-5 flex-shrink-0" />

        {/* Chevron placeholder */}
        <div className="flex-shrink-0 text-gray-300">
          <ChevronRight className="w-4 h-4" />
        </div>

        {/* Carrier logo — hidden when container is very narrow */}
        <div className="flex-shrink-0 w-14 h-10 hidden @3xl:flex items-center justify-center bg-gray-50 rounded-md p-1.5 border border-gray-100 opacity-50">
          <img
            src={carrier.logo}
            alt={carrier.name}
            className="max-w-full max-h-full object-contain"
            onError={(e) => {
              e.currentTarget.src = "/images/carriers/placeholder.svg";
            }}
          />
        </div>

        {/* Carrier name */}
        <div className="w-36 flex-shrink-0">
          <Typography as="span" variant="body" weight="semibold" truncate className="block">
            {carrier.name}
          </Typography>
        </div>

        {/* Loading premium placeholder */}
        <div className="w-36 flex-shrink-0">
          <div className="h-5 w-20 bg-gray-200 rounded animate-pulse" />
        </div>

        {/* Commission placeholder — hidden when container is narrow */}
        <div className="w-28 flex-shrink-0 hidden @4xl:flex justify-center">
          <div className="h-4 w-12 bg-gray-200 rounded animate-pulse" />
        </div>

        {/* Quote ID placeholder — hidden when container is narrow */}
        <div className="w-32 flex-shrink-0 hidden @5xl:block">
          <div className="h-4 w-24 bg-gray-200 rounded animate-pulse" />
        </div>

        {/* Date placeholder — hidden when container is narrow */}
        <div className="w-24 flex-shrink-0 hidden @6xl:block">
          <div className="h-4 w-16 bg-gray-200 rounded animate-pulse" />
        </div>

        {/* Alerts spacer */}
        <div className="w-12 flex-shrink-0 hidden @3xl:block" />

        {/* Loading badge */}
        <div className="w-24 flex-shrink-0 flex justify-center ml-auto">
          <Typography as="span" variant="small" weight="medium" color="text-blue-600" className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 rounded-full">
            <Loader2 className="w-3 h-3 animate-spin" />
            Loading
          </Typography>
        </div>

        {/* Re-rate spacer */}
        <div className="w-10 flex-shrink-0 hidden @3xl:block" />
      </div>
    </div>
  );
}
